import React, { useEffect } from "react";
import { X } from "lucide-react";

const SectionHeading = ({ children }) => (
  <h4 className="text-lg font-semibold text-gray-900">{children}</h4>
);

const SizeGuideModal = ({ open, onClose }) => {
  // Prevent background scrolling while modal is open
  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute inset-x-4 top-10 bottom-10 max-w-5xl mx-auto overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
              Guide
            </p>
            <h3 className="text-2xl font-serif text-gray-900">Size Guide</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
            aria-label="Close size guide"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-8">
          <section className="space-y-3">
            <SectionHeading>Ring sizing at home</SectionHeading>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-medium">Option 1 - Using a ring you own</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Pick a ring that fits the right finger.</li>
                  <li>Measure the inside diameter in millimeters.</li>
                  <li>Match it with the chart below.</li>
                </ol>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-medium">Option 2 - String or paper strip</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Wrap a thin strip around your finger.</li>
                  <li>Mark where it overlaps and measure in millimeters.</li>
                  <li>That length is your finger circumference.</li>
                  <li>Compare with the chart.</li>
                </ol>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700 border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">US Size</th>
                    <th className="px-4 py-3 font-semibold">Diameter (mm)</th>
                    <th className="px-4 py-3 font-semibold">
                      Circumference (mm)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { size: "6", diameter: "16.5", circumference: "52" },
                    { size: "7", diameter: "17.3", circumference: "54" },
                    { size: "8", diameter: "18.1", circumference: "57" },
                    { size: "9", diameter: "18.9", circumference: "59" },
                  ].map((row) => (
                    <tr
                      key={row.size}
                      className="border-t border-gray-200 odd:bg-white even:bg-gray-50"
                    >
                      <td className="px-4 py-3 font-medium">{row.size}</td>
                      <td className="px-4 py-3">{row.diameter}</td>
                      <td className="px-4 py-3">{row.circumference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600">
              If you are between sizes, we suggest choosing the larger size.
            </p>
          </section>

          <section className="space-y-3">
            <SectionHeading>Necklace lengths</SectionHeading>
            <ul className="grid gap-3 md:grid-cols-2 text-sm text-gray-700">
              <li>
                <span className="font-medium">35-40 cm (Choker):</span> Sits
                high on the neck.
              </li>
              <li>
                <span className="font-medium">45 cm (Classic):</span> Falls at
                the collarbone.
              </li>
              <li>
                <span className="font-medium">50-55 cm:</span> Slightly lower,
                everyday style.
              </li>
              <li>
                <span className="font-medium">60+ cm:</span> Long statement
                style.
              </li>
            </ul>
            <p className="text-sm text-gray-600">
              Tip: For layered looks, pick two lengths with a 5-10 cm difference.
            </p>
          </section>

          <section className="space-y-3">
            <SectionHeading>Bracelet fit</SectionHeading>
            <div className="space-y-2 text-sm text-gray-700">
              <p className="font-medium">How to measure</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Wrap a measuring tape around your wrist.</li>
                <li>Add 1-2 cm based on how loose you prefer:</li>
              </ol>
              <ul className="list-disc list-inside pl-1 space-y-1">
                <li>+1 cm for a snug fit</li>
                <li>+1.5 to 2 cm for a relaxed fit</li>
              </ul>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700 border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Fit</th>
                    <th className="px-4 py-3 font-semibold">Wrist (cm)</th>
                    <th className="px-4 py-3 font-semibold">
                      Recommended Size
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { fit: "Small", wrist: "13-15", size: "16-17 cm" },
                    { fit: "Medium", wrist: "15-17", size: "17-18 cm" },
                    { fit: "Large", wrist: "17-19", size: "19-20 cm" },
                  ].map((row) => (
                    <tr
                      key={row.fit}
                      className="border-t border-gray-200 odd:bg-white even:bg-gray-50"
                    >
                      <td className="px-4 py-3 font-medium">{row.fit}</td>
                      <td className="px-4 py-3">{row.wrist}</td>
                      <td className="px-4 py-3">{row.size}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeading>Earrings</SectionHeading>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Studs are shown in millimeters (3 mm - 10 mm).</li>
              <li>Hoops are measured by outer diameter (10 mm - 60 mm).</li>
              <li>Drops list the total hanging length in centimeters.</li>
            </ul>
          </section>

          <section className="space-y-2 text-sm text-gray-700">
            <SectionHeading>Quick tips</SectionHeading>
            <ul className="list-disc list-inside space-y-1">
              <li>All measurements are approximate; slight variation is normal.</li>
              <li>If you are between two sizes, choose the larger one.</li>
              <li>Common gift sizes: Ring 7, Bracelet 17 cm, Necklace 45 cm.</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SizeGuideModal;
